import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Program } from '@nxcr-org/web-api-interface/lib/billing_service_pb'

interface ProgramFormProps {
  program?: Program
  clientId: string
  open: boolean
  onClose: () => void
  onSave: (program: Program) => Promise<void>
}

interface ProgramValues {
  name: string
  description: string
  make: string
  model: string
  type: string
  programGroup: string
  active: boolean
  hidden: boolean
  featured: boolean
  featuredOrder: string
}

const textFields = [
  { name: 'name', label: 'Name', required: true },
  { name: 'description', label: 'Description', required: true },
  { name: 'make', label: 'Make', required: true },
  { name: 'model', label: 'Model', required: true },
] as const

const selectFields = [
  { name: 'type', label: 'Type', options: ['electric', 'gas'] },
  { name: 'programGroup', label: 'Program group', options: ['car', 'suv', 'truck'] },
] as const

export const ProgramForm = ({ program, clientId, open, onClose, onSave }: ProgramFormProps) => {
  const [saveError, setSaveError] = useState(false)
  const mounted = useRef(false)
  const saving = useRef(false)
  const saved = useRef(false)
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<ProgramValues>({
    defaultValues: {
      name: program?.getName() ?? '',
      description: program?.getDescription() ?? '',
      make: program?.getMake() ?? '',
      model: program?.getModel() ?? '',
      type: program?.getType() ?? '',
      programGroup: program?.getProgramGroup() ?? '',
      active: program?.getActive() ?? false,
      hidden: program?.getHidden() ?? false,
      featured: program?.getFeatured() ?? false,
      featuredOrder: String(program?.getFeaturedOrder() ?? 0),
    },
  })
  const featured = useWatch({ control, name: 'featured' })

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!open || (!isDirty && !isSubmitting)) return
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (saved.current) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [open, isDirty, isSubmitting])

  const close = () => {
    if (saving.current || isSubmitting) return
    if (!saved.current && isDirty && !window.confirm('Discard unsaved program changes?')) return
    onClose()
  }

  const submit = async (values: ProgramValues) => {
    if (saving.current) return
    saving.current = true
    setSaveError(false)
    try {
      // Preserve non-editable generated fields when sending the full update.
      const updated = program ? Program.deserializeBinary(program.serializeBinary()) : new Program()
      updated.setName(values.name.trim())
      updated.setDescription(values.description.trim())
      updated.setMake(values.make.trim())
      updated.setModel(values.model.trim())
      updated.setType(values.type.trim())
      updated.setProgramGroup(values.programGroup.trim())
      updated.setActive(values.active)
      updated.setHidden(values.hidden)
      updated.setFeatured(values.featured)
      updated.setFeaturedOrder(values.featured ? Number(values.featuredOrder) : (program?.getFeaturedOrder() ?? 0))
      updated.setClientId(clientId)
      updated.clearPricingOptionsList()
      await onSave(updated)
      saved.current = true
      if (mounted.current) {
        reset(values)
        onClose()
      }
    } catch {
      if (mounted.current) setSaveError(true)
    } finally {
      saving.current = false
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth disableEscapeKeyDown={isSubmitting}>
      <form onSubmit={handleSubmit(submit)} noValidate>
        <DialogTitle>{program ? 'Edit program' : 'Create program'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {saveError && (
              <Alert severity="error">Unable to save the program. Your changes are preserved. Please try again.</Alert>
            )}
            {textFields.map(({ name, label, required }) => (
              <Controller
                key={name}
                name={name}
                control={control}
                rules={{
                  validate: (value) => !required || value.trim().length > 0 || `${label} is required`,
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={label}
                    required={required}
                    fullWidth
                    disabled={isSubmitting}
                    multiline={name === 'description'}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            ))}
            {selectFields.map(({ name, label, options }) => (
              <Controller
                key={name}
                name={name}
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label={label} fullWidth disabled={isSubmitting}>
                    <MenuItem value="">Select {label.toLowerCase()}</MenuItem>
                    {options.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                )}
              />
            ))}
            {(['active', 'hidden', 'featured'] as const).map((name) => (
              <Controller
                key={name}
                name={name}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label={name.charAt(0).toUpperCase() + name.slice(1)}
                    control={
                      <Checkbox
                        name={field.name}
                        inputRef={field.ref}
                        checked={field.value}
                        onBlur={field.onBlur}
                        onChange={(_, checked) => field.onChange(checked)}
                        disabled={isSubmitting}
                      />
                    }
                  />
                )}
              />
            ))}
            {featured && (
              <Controller
                name="featuredOrder"
                control={control}
                rules={{
                  validate: (value, values) =>
                    !values.featured ||
                    (/^\d+$/.test(value) &&
                      Number.isSafeInteger(Number(value)) &&
                      Number(value) <= 2147483647) ||
                    'Enter a whole number from 0 to 2147483647',
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Featured order"
                    required
                    disabled={isSubmitting}
                    slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
            {isSubmitting && (
              <Stack direction="row" spacing={1} role="status" alignItems="center">
                <CircularProgress size={18} />
                <span>Saving program…</span>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>Save program</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default ProgramForm
